/* eslint-disable react/jsx-max-depth */
import { ROUTES, LOCAL_STORAGE_KEY, queryOptions } from 'constants';
import React, { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Routes,
  Route,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { useTeamColors } from 'hooks';
import { logDebug, logError } from 'utils/logger';
import {
  handleIOSStandaloneReload,
  clearIOSStandaloneFlag,
  setupVisibilityRefresh,
  setupInstallPrompt,
} from 'utils/iosStandalone';
import {
  updatePageIcons,
  updateManifest,
  updatePageIconsHybrid,
  clearIconCache,
} from 'utils/pwaIcons';
import MainPage from 'pages/MainPage/MainPage';
import ChampionshipPage from 'pages/Championship/ChampionshipPage';
import RestorePassword from 'pages/RestorePassword/RestorePassword';
import Signup from 'pages/Signup/Signup';
import ChangePassword from 'pages/ChangePassword/ChangePassword';
import CustomEvents from 'pages/CustomEvents/CustomEvents';
import { UpcomingGames } from 'pages/UpcomingGames';
import Policy from 'pages/Policy/Policy';
import Vitals from 'pages/Vitals/Vitals';
import NotFound from 'pages/NotFound/NotFound';
import { NewsArticle } from 'components/News';
import { getSiteInfoConfigAction } from 'redux/config/actionCreators.js';
import { setUserInfoAction } from 'redux/user/actionCreators.js';
import { ToastContainer } from 'react-toastify';
import { ErrorBoundary } from 'components/ErrorBoundary/ErrorBoundary';
import { api } from 'api/api';
// import { InstallPrompt } from 'components/InstallPrompt/InstallPrompt';
import { ThemeProvider } from './ThemeProvider';
import { Wrapper } from './Wrapper';
import { GlobalStyle } from './GlobalStyle';
// NOTE: Interactive way to test sentry error hanling (needs to run in prod) - use responsibly (don't deploy permananty !!)
// import SentryTest from 'components/SentryTest';

const hostName = window.location.hostname;
const siteUrl = hostName;

const EventRedirect = ({ setEventLeague }) => {
  const navigate = useNavigate();
  const { espnId } = useParams();
  const [searchParams] = useSearchParams();
  const teamName = searchParams.get('team_name');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add noindex meta tag to prevent Google from indexing redirect pages
  useEffect(() => {
    document.title = 'Redirecting...';
  }, []);

  useEffect(() => {
    const fetchEventData = async () => {
      if (!espnId) return;

      try {
        setIsLoading(true);
        const response = await api.events.getByEspnId(espnId, teamName);
        logDebug('ESPN event fetch response:', response.data);
        const { league, leagueId, team, teamId } = response.data;

        setEventLeague(league);

        // Don't include eventId in URL - let the team schedule load and show current game
        // The backend may return an eventId that doesn't exist in the current schedule
        // which causes the app to show the wrong game
        const searchParams = new URLSearchParams({
          league,
          leagueId,
          team,
          teamId,
        });

        // Navigate without eventId - will show current/upcoming game for this team
        navigate(`/?${searchParams.toString()}`, {
          state: {
            league,
            leagueId,
            team,
            teamId,
            fromDirectLink: true,
          },
          replace: true,
        });
      } catch (error) {
        logError('Failed to fetch ESPN event data', error, {
          espnId,
          teamName,
        });
        setError(error);

        // If event not found but we have team name, try to navigate to team page
        // The API might return team info even if the specific event is not found
        if (error.response?.data) {
          const { league, leagueId, team, teamId } = error.response.data;
          if (league && leagueId && team && teamId) {
            // Navigate to team page without eventId - will show current event
            const searchParams = new URLSearchParams({
              league,
              leagueId,
              team,
              teamId,
            });
            setEventLeague(league);
            navigate(`/?${searchParams.toString()}`, { replace: true });
            return;
          }
        }

        // Fallback: just go to home page
        navigate('/', { replace: true });
      } finally {
        setIsLoading(false);
      }
    };

    fetchEventData();
  }, [espnId, teamName, navigate, setEventLeague]);

  if (!teamName) {
    return (
      <>
        <Helmet>
          <meta content="noindex, nofollow" name="robots" />
        </Helmet>
        <div>Missing team name parameter</div>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <Helmet>
          <meta content="noindex, nofollow" name="robots" />
        </Helmet>
        <div>Loading event data...</div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <meta content="noindex, nofollow" name="robots" />
        </Helmet>
        <div>Error loading event. Redirecting to home...</div>
      </>
    );
  }

  return (
    <Helmet>
      <meta content="noindex, nofollow" name="robots" />
    </Helmet>
  );
};

/**
 * Main App component that serves as the root of the application.
 * @component
 * @returns {React.ReactElement} The root application component with routing and state management setup
 * @example
 * ```jsx
 * <App />
 * ```
 */
const App = () => {
  const dispatch = useDispatch();
  const user = localStorage.getItem(`${LOCAL_STORAGE_KEY}access-token`);

  // Use ref instead of state to prevent App re-renders when eventLeague changes
  // This prevents MainPage from unmounting/remounting repeatedly which causes iOS crashes
  const eventLeagueRef = React.useRef('');
  const setEventLeagueTimeoutRef = React.useRef(null);

  const setEventLeague = React.useCallback((league) => {
    // CRITICAL: Debounce to prevent rapid successive calls that crash iOS Safari
    // Clear any pending timeout
    if (setEventLeagueTimeoutRef.current) {
      clearTimeout(setEventLeagueTimeoutRef.current);
    }

    // Only update if league actually changed
    if (eventLeagueRef.current === league) {
      return;
    }

    eventLeagueRef.current = league;

    // Debounce the event dispatch by 100ms to prevent rapid successive calls
    setEventLeagueTimeoutRef.current = setTimeout(() => {
      const event = new CustomEvent('eventLeagueChanged', {
        detail: { league },
      });
      window.dispatchEvent(event);
      setEventLeagueTimeoutRef.current = null;
    }, 100);
  }, []);

  const { mainColor, altColor } = useTeamColors();
  const queryClient = useMemo(
    () => new QueryClient({ defaultOptions: queryOptions }),
    []
  );

  useEffect(() => {
    document.documentElement.style.setProperty('--toast-bg-color', mainColor);
    document.documentElement.style.setProperty('--toast-alt-color', altColor);
  }, [mainColor, altColor]);

  useEffect(() => {
    if (user) {
      const parsedUserInfo = JSON.parse(user);
      dispatch(setUserInfoAction(parsedUserInfo));

      // Trigger push notification modal after login/signup
      // MainPage will listen for this event and show the modal
      console.log(
        '[App] User logged in, dispatching userLoggedIn event in 1.5s',
        parsedUserInfo.user_id
      );
      setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent('userLoggedIn', {
            detail: { userId: parsedUserInfo.user_id },
          })
        );
      }, 1500);
    }
  }, [user, dispatch]);

  // Push notification token fetching is now handled through the
  // PushNotificationPermissionModal after user consent

  useEffect(() => {
    dispatch(getSiteInfoConfigAction(siteUrl));
  }, [dispatch]);

  // Get configuration from Redux store
  const configuration = useSelector((state) => state.configuration?.data);

  // Update PWA icons when configuration loads
  // Use ref to track if we've already updated to prevent loops
  const iconsUpdatedRef = React.useRef(false);

  useEffect(() => {
    if (configuration && !iconsUpdatedRef.current) {
      try {
        // Clear cache to ensure fresh checks
        clearIconCache();

        // Use the new hybrid approach: defaults immediately, then async site-specific
        updatePageIconsHybrid(configuration);
        updateManifest(configuration);
        iconsUpdatedRef.current = true;
      } catch (error) {
        console.error('[PWA Icons] Error updating icons:', error);
        // Fallback to the original function if the hybrid one fails
        try {
          updatePageIcons(configuration);
          updateManifest(configuration);
        } catch (fallbackError) {
          console.error('[PWA Icons] Fallback also failed:', fallbackError);
        }
      }
    }
  }, [configuration]);

  // PWA-specific features (ONLY for installed PWAs)
  // These have strict checks and will NOT run in regular browsers
  useEffect(() => {
    // iOS PWA: Force reload on initial app open (standalone mode only)
    // Has internal check: MUST be iOS AND standalone
    handleIOSStandaloneReload();
    clearIOSStandaloneFlag();

    // Mobile PWA: Auto-refresh when switching back after 5+ seconds
    // Has internal check: MUST be mobile AND standalone
    setupVisibilityRefresh(5);

    // Setup install prompt for PWA (safe for all browsers)
    setupInstallPrompt();
  }, []);

  return (
    <ThemeProvider>
      <StyledThemeProvider theme={{ mainColor }}>
        <GlobalStyle />
        <QueryClientProvider client={queryClient}>
          <ToastContainer autoClose={5000} />
          {/* <InstallPrompt /> */}
          <ErrorBoundary>
            <Wrapper>
              <Routes>
                <Route
                  element={<MainPage setEventLeague={setEventLeague} />}
                  exact
                  path="/"
                />
                <Route
                  element={<ChampionshipPage />}
                  exact
                  path={`/${ROUTES.CHAMPIONSHIPS}`}
                />
                <Route
                  element={<RestorePassword />}
                  exact
                  path={`/${ROUTES.RESTORE_PASSWORD}`}
                />
                <Route element={<Signup />} exact path={`/${ROUTES.SIGNUP}`} />
                <Route
                  element={<ChangePassword />}
                  exact
                  path={`/${ROUTES.CHANGE_PASSWORD}`}
                />
                <Route
                  element={<CustomEvents />}
                  exact
                  path={`/${ROUTES.CUSTOM_EVENTS}`}
                />
                <Route
                  element={<UpcomingGames />}
                  exact
                  path={`/${ROUTES.UPCOMING_GAMES}`}
                />
                <Route element={<Policy />} exact path={`/${ROUTES.POLICY}`} />
                <Route element={<Policy />} exact path={`/${ROUTES.PRIVACY}`} />
                <Route element={<Policy />} exact path={`/${ROUTES.SUPPORT}`} />
                <Route element={<Vitals />} exact path={`/${ROUTES.VITALS}`} />
                <Route
                  element={<NewsArticle />}
                  exact
                  path={`/${ROUTES.NEWS}/:newsId`}
                />
                <Route
                  element={<EventRedirect setEventLeague={setEventLeague} />}
                  exact
                  path="/e/:espnId"
                />
                <Route
                  element={
                    <MainPage
                      eventLeague={eventLeagueRef.current}
                      setEventLeague={setEventLeague}
                    />
                  }
                  exact
                  path="/event/:league/:leagueId/:teamId/:eventId"
                />
                {/* Tournament Routes */}
                <Route
                  element={() => {
                    window.location.href = '/tournament/madness/';
                    return null;
                  }}
                  exact
                  path="/tournament"
                />
                <Route
                  element={() => {
                    window.location.href = '/tournament/madness/';
                    return null;
                  }}
                  exact
                  path="/tournament/madness"
                />
                <Route
                  element={() => {
                    window.location.href = '/tournament/playoffs/';
                    return null;
                  }}
                  exact
                  path="/tournament/playoffs"
                />
                <Route element={<NotFound />} path="*" />
              </Routes>
            </Wrapper>
          </ErrorBoundary>
        </QueryClientProvider>
      </StyledThemeProvider>
      {/* <SentryTest /> */}
    </ThemeProvider>
  );
};

export default App;
